import React, { useState, useEffect } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Loader, Shield, Copy, Check, AlertCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuthenticationState } from '../../../store/useAuthenticationState';
import { createPhpSession } from './createSession';
import { hasBlockingComplianceItems } from './complianceGate';

const SETUP_TOTP_WITH_TOKEN = gql`
  mutation SetupTOTPWithToken($enrollmentToken: String!) {
    setupTOTP(enrollmentToken: $enrollmentToken) {
      qrCode
      secret
      backupCodes
    }
  }
`;

const VERIFY_TOTP_SETUP_WITH_TOKEN = gql`
  mutation VerifyTOTPSetupWithToken($code: String!, $enrollmentToken: String!) {
    verifyTOTPSetup(code: $code, enrollmentToken: $enrollmentToken) {
      success
      backupCodes
    }
  }
`;

const CLAIM_PENDING_TOTP_ENROLLMENT = gql`
  mutation ClaimPendingTOTPEnrollment {
    claimPendingTOTPEnrollment {
      token
      expiresAt
    }
  }
`;

const PROMOTE_COMPLIANCE_SESSION = gql`
  mutation PromoteComplianceSession {
    promoteComplianceSession {
      promoted
      pendingCompliance
      blockingCount
      token
    }
  }
`;

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      id
      item
      dueInDays
      required
    }
  }
`;

type TOTPEnrollmentProps = {
  embedded?: boolean;
  initialEnrollmentToken?: string | null;
  onComplete?: () => void | Promise<void>;
};

export const TOTPEnrollment = ({
  embedded = false,
  initialEnrollmentToken = null,
  onComplete
}: TOTPEnrollmentProps = {}) => {
  const [searchParams] = useSearchParams();
  const { authenticationState, setAuthenticationStateJWT } = useAuthenticationState();

  const [step, setStep] = useState<'validate' | 'setup' | 'verify' | 'complete'>('validate');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);

  const [setupTOTP] = useMutation(SETUP_TOTP_WITH_TOKEN);
  const [verifyTOTPSetup] = useMutation(VERIFY_TOTP_SETUP_WITH_TOKEN);
  const [claimEnrollmentToken] = useMutation(CLAIM_PENDING_TOTP_ENROLLMENT);
  const [promoteComplianceSession] = useMutation(PROMOTE_COMPLIANCE_SESSION);
  const [fetchCompliance] = useLazyQuery(GET_USER_COMPLIANCE_STATUS, {
    fetchPolicy: 'network-only'
  });

  const resolveDashboardDestination = (sessionOk: boolean) => {
    return '/dashboard';
  };

  useEffect(() => {
    const initializeEnrollment = async () => {
      setLoading(true);
      setError('');
      try {
        const providedToken = `${initialEnrollmentToken || ''}`.trim() || searchParams.get('token');
        if (providedToken) {
          setEnrollmentToken(providedToken);
          await handleSetup(providedToken, { skipLoading: true });
          setLoading(false);
          return;
        }

        const { data } = await claimEnrollmentToken();
        const token = data?.claimPendingTOTPEnrollment?.token;
        if (!token) {
          setError('No enrollment link available. Please contact your administrator.');
          setStep('validate');
          return;
        }
        setEnrollmentToken(token);
        await handleSetup(token, { skipLoading: true });
      } catch (err: any) {
        setError(err.message || 'Unable to load your enrollment link. Please contact your administrator.');
        setStep('validate');
      }
      setLoading(false);
    };
    initializeEnrollment();
  }, [initialEnrollmentToken, searchParams]);

  const handleSetup = async (tokenOverride?: string, options?: { skipLoading?: boolean }) => {
    const tokenToUse = tokenOverride ?? enrollmentToken;
    if (!tokenToUse) return;
    
    if (!options?.skipLoading) setLoading(true);
    setError('');
    try {
      const result = await setupTOTP({
        variables: { enrollmentToken: tokenToUse }
      });
      setQrCode(result.data.setupTOTP.qrCode);
      setSecret(result.data.setupTOTP.secret);
      setStep('verify');
    } catch (err: any) {
      if (err.message.includes('Enrollment token is required')) {
        setError('Invalid enrollment link. Please contact your administrator for a new link.');
      } else if (err.message.includes('Invalid enrollment token')) {
        setError('This enrollment link has expired or is invalid. Please contact your administrator for a new link.');
      } else if (err.message.includes('Azure AD users')) {
        setError('Azure AD users cannot change their authentication method. Your authentication is managed through Microsoft Azure.');
      } else if (err.message.includes('already has TOTP enabled')) {
        setError('TOTP is already enabled for your account. You can manage your TOTP settings in your account settings.');
      } else {
        setError(err.message || 'Failed to setup TOTP');
      }
      setStep('validate');
    }
    if (!options?.skipLoading) setLoading(false);
  };

  const handleVerify = async () => {
    if (!enrollmentToken) return;
    
    setLoading(true);
    setError('');
    try {
      const result = await verifyTOTPSetup({
        variables: { 
          code: verificationCode,
          enrollmentToken 
        }
      });
      if (result.data.verifyTOTPSetup.success) {
        setBackupCodes(result.data.verifyTOTPSetup.backupCodes);
        setStep('complete');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    }
    setLoading(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = async () => {
    const returnTarget = searchParams.get('return');

    if (embedded) {
      if (typeof onComplete === 'function') {
        await onComplete();
      }
      return;
    }

    try {
      const result = await fetchCompliance();
      const complianceItems = result.data?.getUserComplianceStatus || [];
      if (hasBlockingComplianceItems(complianceItems)) {
        window.location.href = returnTarget || '/dashboard';
        return;
      }
    } catch (err) {
      console.error('Failed to refresh compliance status', err);
      window.location.href = returnTarget || '/dashboard';
      return;
    }

    let sessionToken = authenticationState;
    if (authenticationState) {
      try {
        const promotionResult = await promoteComplianceSession();
        const promotedToken = promotionResult.data?.promoteComplianceSession?.token;
        if (promotedToken) {
          setAuthenticationStateJWT(promotedToken);
          sessionToken = promotedToken;
        }
      } catch (promotionError) {
        console.error('Failed to promote compliance session', promotionError);
      }
    }

    if (!sessionToken) {
      window.location.href = returnTarget || '/dashboard';
      return;
    }

    const sessionOk = await createPhpSession(sessionToken);
    window.location.href = returnTarget || resolveDashboardDestination(sessionOk);
  };

  const containerClasses = embedded ? 'w-full h-full p-0' : 'max-w-2xl mx-auto p-6';
  const cardClasses = embedded ? 'bg-white rounded-lg shadow p-6 h-full overflow-auto' : 'bg-white rounded-lg shadow p-6';

  if (step === 'validate') {
    return (
      <div className={containerClasses}>
        <div className={cardClasses}>
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold">TOTP Enrollment</h2>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}
          
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Validating your enrollment token...
            </p>
            <Loader className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className={containerClasses}>
        <div className={cardClasses}>
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold">Setup Two-Factor Authentication</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Step 1: Scan QR Code</h3>
              <p className="text-sm text-gray-600 mb-4">
                Scan this QR code with your authenticator app (Microsoft or Google Authenticator)
              </p>
              <div className="flex justify-center bg-gray-50 p-4 rounded">
                <img src={qrCode} alt="TOTP QR Code" className="w-48 h-48" />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Manual Entry</h3>
              <p className="text-sm text-gray-600 mb-2">
                If you can't scan the QR code, enter this secret manually:
              </p>
              <div className="flex items-center bg-gray-100 p-2 rounded">
                <code className="flex-1 text-sm font-mono">{secret}</code>
                <button
                  onClick={() => copyToClipboard(secret)}
                  className="ml-2 p-1 hover:bg-gray-200 rounded"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Step 2: Verify</h3>
              <p className="text-sm text-gray-600 mb-2">
                Enter the 6-digit code from your authenticator app:
              </p>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="w-full px-3 py-2 border rounded text-center text-lg font-mono"
              />
              {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
              <button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? <Loader className="h-5 w-5 animate-spin mx-auto" /> : 'Verify and Enable'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className={containerClasses}>
        <div className={cardClasses}>
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold">TOTP Setup Complete</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <h3 className="text-green-800 font-semibold">✓ Two-Factor Authentication Enabled</h3>
              <p className="text-green-700 text-sm mt-1">
                Your account is now protected with TOTP authentication.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Backup Codes</h3>
              <p className="text-sm text-red-600 mb-4">
                ⚠️ Save these codes in a secure place. You won't be able to see them again!
              </p>
              <div className="bg-gray-50 p-4 rounded space-y-2">
                {backupCodes.map((code, index) => (
                  <div key={index} className="font-mono text-sm">
                    {index + 1}. {code}
                  </div>
                ))}
              </div>
              <button
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
                className="mt-4 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                <Copy className="h-4 w-4 inline mr-2" />
                Copy All Codes
              </button>
            </div>

            <button
              onClick={handleComplete}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Return to Main Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
